import { useEffect } from "react";

/**
 * Performance monitoring component using Web Vitals
 * Reports Core Web Vitals metrics to console (can be sent to analytics)
 */
export function PerformanceMonitor() {
  useEffect(() => {
    if (typeof window === "undefined" || !("PerformanceObserver" in window)) {
      return;
    }

    // Only run in production
    if (import.meta.env.DEV) {
      return;
    }

    // Measure First Contentful Paint (FCP)
    const fcpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.name === "first-contentful-paint") {
          console.log("✅ FCP:", Math.round(entry.startTime), "ms");
          // Send to analytics: analytics.track('FCP', entry.startTime);
        }
      }
    });

    // Measure Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      console.log("✅ LCP:", Math.round(lastEntry.startTime), "ms");
      // Send to analytics: analytics.track('LCP', lastEntry.startTime);
    });

    // Measure Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }
      console.log("✅ CLS:", clsValue.toFixed(3));
      // Send to analytics: analytics.track('CLS', clsValue);
    });

    // Measure First Input Delay (FID)
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidEntry = entry as PerformanceEventTiming;
        const fid = fidEntry.processingStart - fidEntry.startTime;
        console.log("✅ FID:", Math.round(fid), "ms");
        // Send to analytics: analytics.track('FID', fid);
      }
    });

    try {
      fcpObserver.observe({ type: "paint", buffered: true });
      lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
      clsObserver.observe({ type: "layout-shift", buffered: true });
      fidObserver.observe({ type: "first-input", buffered: true });
    } catch (e) {
      // Some browsers might not support all metrics
      console.warn("Performance monitoring not fully supported:", e);
    }

    // Cleanup
    return () => {
      fcpObserver.disconnect();
      lcpObserver.disconnect();
      clsObserver.disconnect();
      fidObserver.disconnect();
    };
  }, []);

  return null; // This component doesn't render anything
}

/**
 * Report navigation timing metrics using modern Navigation Timing Level 2 API
 */
export function reportNavigationMetrics() {
  if (typeof window === "undefined" || !window.performance) {
    return;
  }

  window.addEventListener("load", () => {
    setTimeout(() => {
      // Use modern PerformanceNavigationTiming API
      const perfEntries = performance.getEntriesByType("navigation");
      if (perfEntries.length > 0) {
        const navTiming = perfEntries[0] as PerformanceNavigationTiming;

        const pageLoadTime = navTiming.loadEventEnd - navTiming.fetchStart;
        const dnsTime = navTiming.domainLookupEnd - navTiming.domainLookupStart;
        const connectTime = navTiming.connectEnd - navTiming.connectStart;
        const responseTime = navTiming.responseEnd - navTiming.requestStart;
        const domInteractive = navTiming.domInteractive - navTiming.fetchStart;
        const domComplete = navTiming.domComplete - navTiming.fetchStart;

        console.group("📊 Navigation Timing (Level 2)");
        console.log("Page Load Time:", Math.round(pageLoadTime), "ms");
        console.log("DNS Lookup:", Math.round(dnsTime), "ms");
        console.log("Connection:", Math.round(connectTime), "ms");
        console.log("Response:", Math.round(responseTime), "ms");
        console.log("DOM Interactive:", Math.round(domInteractive), "ms");
        console.log("DOM Complete:", Math.round(domComplete), "ms");
        console.groupEnd();

        // Send to analytics
        // analytics.track('PageLoad', { pageLoadTime, dnsTime, connectTime, responseTime });
      }
    }, 0);
  });
}
